/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import {format} from 'date-fns';
import React, {useEffect, useState} from 'react';
import {LoadingSpinner} from '../../../components/LoadingSpinner';
import {useToast} from '../../../hooks/useToast';
import type {AgentDocument} from '../../../types/agent';
import {useDeleteAgentDocument} from '../hooks/useDeleteAgentDocument';
import {useGetAgentDocuments} from '../hooks/useGetAgentDocuments';
import {useGetAgentDocumentUploadUrl} from '../hooks/useGetAgentDocumentUploadUrl';
import {useGetAgentDocumentViewUrl} from '../hooks/useGetAgentDocumentViewUrl';

type AgentDocumentsProps = {
  isSaved: boolean;
  agentId?: string;
};

const ALLOWED_FILE_TYPES = ['.pdf', '.txt'];

export const AgentDocuments: React.FC<AgentDocumentsProps> = ({isSaved, agentId}) => {
  const {fetch: fetchDocuments, documents, isLoading: isLoadingDocuments} = useGetAgentDocuments();
  const {fetch: fetchUploadUrl} = useGetAgentDocumentUploadUrl();
  const {fetch: fetchViewUrl} = useGetAgentDocumentViewUrl();
  const {del} = useDeleteAgentDocument();
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [documentModalLoading, setDocumentModalLoading] = useState(false);
  const [newDocumentFile, setNewDocumentFile] = useState<File | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteModalLoading, setDeleteModalLoading] = useState(false);
  const [docToDelete, setDocToDelete] = useState<AgentDocument | null>(null);
  const {toastSuccess, toastError} = useToast();
  const [sortField, setSortField] = useState<keyof AgentDocument>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [viewLoadingDoc, setViewLoadingDoc] = useState<string | null>(null);

  useEffect(() => {
    if (agentId) {
      void fetchDocuments(agentId);
    }
  }, [agentId, fetchDocuments]);

  const handleDocumentModalOpen = () => {
    setNewDocumentFile(null);
    setDocumentModalOpen(true);
  };

  const handleDocumentUpload = async () => {
    if (!newDocumentFile || !agentId) return;
    setDocumentModalLoading(true);

    try {
      const {url} = await fetchUploadUrl(agentId, newDocumentFile.name);
      await fetch(url, {
        method: 'PUT',
        headers: {'Content-Type': newDocumentFile.type || 'application/octet-stream'},
        body: newDocumentFile,
      });
      toastSuccess('File uploaded successfully.');
      await fetchDocuments(agentId);
    } catch (err) {
      toastError('File upload failed. Please try again.');
      console.error('File upload failed:', err);
    } finally {
      setDocumentModalLoading(false);
      setDocumentModalOpen(false);
    }
  };

  const handleDeleteClick = (doc: AgentDocument) => {
    setDocToDelete(doc);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!docToDelete || !agentId) return;
    setDeleteModalLoading(true);

    try {
      await del(agentId, docToDelete.filename);
      await fetchDocuments(agentId);
      toastSuccess('File deleted successfully.');
    } catch (err) {
      toastError('File deletion failed. Please try again.');
      console.error('File deletion failed:', err);
    } finally {
      setDeleteModalLoading(false);
      setDeleteModalOpen(false);
      setDocToDelete(null);
    }
  };

  const handleViewClick = async (doc: AgentDocument) => {
    if (!agentId) return;
    setViewLoadingDoc(doc.filename);
    try {
      const {url} = await fetchViewUrl(agentId, doc.filename);
      window.open(url, '_blank');
    } catch (err) {
      toastError('Failed to open document. Please try again.');
      console.error('Error opening document:', err);
    } finally {
      setViewLoadingDoc(null);
    }
  };

  const sortedDocuments = documents.slice().sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSortChange = (field: keyof AgentDocument) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortField(field);
    setSortDirection(isAsc ? 'desc' : 'asc');
  };

  // TODO: `isLoading`
  // TODO: `isLoadingDelete`
  // TODO: `isLoadingUrl`

  return (
    <Stack spacing={2}>
      {!isSaved && <Alert severity={'info'}>Please save the agent before uploading documents.</Alert>}
      <Box textAlign={'right'}>
        <Button
          variant={'contained'}
          color={'primary'}
          onClick={handleDocumentModalOpen}
          startIcon={<FileUploadIcon />}
          disabled={!isSaved || isLoadingDocuments}
        >
          Upload File
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'filename'}
                  direction={sortField === 'filename' ? sortDirection : 'asc'}
                  onClick={() => handleSortChange('filename')}
                >
                  Filename
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'size'}
                  direction={sortField === 'size' ? sortDirection : 'asc'}
                  onClick={() => handleSortChange('size')}
                >
                  Size
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'updatedAt'}
                  direction={sortField === 'updatedAt' ? sortDirection : 'asc'}
                  onClick={() => handleSortChange('updatedAt')}
                >
                  Last Updated
                </TableSortLabel>
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedDocuments.length > 0 ? (
              sortedDocuments.map((doc) => (
                <TableRow key={doc.filename}>
                  <TableCell>
                    <Link component={'button'} onClick={() => handleViewClick(doc)}>
                      {doc.filename}
                    </Link>
                    {viewLoadingDoc === doc.filename && <LoadingSpinner size={'xs'} style={{marginLeft: 1}} />}
                  </TableCell>
                  <TableCell>{(doc.size / (1024 * 1024)).toFixed(1)} MB</TableCell>
                  <TableCell>{format(new Date(doc.updatedAt), 'dd/MM/yyyy HH:mm')}</TableCell>
                  <TableCell align={'right'}>
                    <IconButton color={'error'} onClick={() => handleDeleteClick(doc)} size={'small'}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align={'center'}>
                  <Button
                    variant={'text'}
                    color={'primary'}
                    onClick={handleDocumentModalOpen}
                    startIcon={<AddIcon />}
                    disabled={!isSaved}
                    loading={isLoadingDocuments}
                  >
                    Add a Document
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity={'info'} sx={{mt: 2}}>
        <AlertTitle>File changes are instantly saved to the agent</AlertTitle>
        These updates will be preserved regardless of any unsaved configurations to the agent.
      </Alert>

      <Dialog
        open={documentModalOpen}
        onClose={() => !documentModalLoading && setDocumentModalOpen(false)}
        fullWidth
        maxWidth={'sm'}
        disableEscapeKeyDown={documentModalLoading}
      >
        <DialogTitle>Upload File</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant={'body2'}>These files will be included in the knowledge base of your agent.</Typography>
            <Typography variant={'body2'}>
              Currently the only supported file types are {ALLOWED_FILE_TYPES.join(' and ')}.
            </Typography>
            <TextField
              type={'file'}
              fullWidth
              inputProps={{accept: ALLOWED_FILE_TYPES.join(',')}}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.files && e.target.files.length > 0) {
                  setNewDocumentFile(e.target.files[0]);
                }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentModalOpen(false)} disabled={documentModalLoading}>
            Cancel
          </Button>
          <Button
            variant={'contained'}
            startIcon={<FileUploadIcon />}
            onClick={handleDocumentUpload}
            disabled={!newDocumentFile}
            loading={documentModalLoading}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteModalOpen}
        onClose={() => !deleteModalLoading && setDeleteModalOpen(false)}
        disableEscapeKeyDown={deleteModalLoading}
      >
        <DialogTitle>Are you sure you wish to delete this file?</DialogTitle>
        <DialogContent>
          <Typography>{docToDelete?.filename}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)} disabled={deleteModalLoading}>
            Cancel
          </Button>
          <Button variant={'contained'} color={'error'} onClick={handleConfirmDelete} loading={deleteModalLoading}>
            Yes, I'm sure
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
